import { useMemo, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  DAYS_ORDERED,
  type DayHours,
  type DayKey,
  type EditorProps,
  type OpeningHoursConfig,
  type SpecialClosure,
  type TimePeriod,
} from './types';

const MAX_PERIODS = 3;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function ensureDay(config: OpeningHoursConfig, key: DayKey): DayHours {
  const existing = config.regularHours[key];
  if (existing) return existing;
  const meta = DAYS_ORDERED.find((d) => d.key === key)!;
  return { day: meta.day, dayShort: meta.dayShort, closed: true };
}

function periodInvalid(p: TimePeriod): boolean {
  if (!p.open || !p.close) return true;
  return p.open >= p.close;
}

export function OpeningHoursEditor({ config, onChange }: EditorProps<OpeningHoursConfig>) {
  const [tab, setTab] = useState<'regular' | 'closures'>('regular');
  const [advancedOpen, setAdvancedOpen] = useState<Record<DayKey, boolean>>(
    {} as Record<DayKey, boolean>,
  );

  const updateDay = (key: DayKey, patch: Partial<DayHours>) => {
    const current = ensureDay(config, key);
    onChange({
      ...config,
      regularHours: {
        ...config.regularHours,
        [key]: { ...current, ...patch },
      },
    });
  };

  const updatePeriod = (key: DayKey, idx: number, patch: Partial<TimePeriod>) => {
    const current = ensureDay(config, key);
    const periods = [...(current.periods ?? [])];
    periods[idx] = { ...periods[idx], ...patch } as TimePeriod;
    updateDay(key, { periods });
  };

  const addPeriod = (key: DayKey) => {
    const current = ensureDay(config, key);
    const periods = [...(current.periods ?? [])];
    if (periods.length >= MAX_PERIODS) return;
    periods.push({ open: '09:00', close: '18:00' });
    updateDay(key, { periods, closed: false });
  };

  const removePeriod = (key: DayKey, idx: number) => {
    const current = ensureDay(config, key);
    const periods = [...(current.periods ?? [])];
    periods.splice(idx, 1);
    updateDay(key, { periods });
  };

  const toggleClosed = (key: DayKey, closed: boolean) => {
    const current = ensureDay(config, key);
    updateDay(key, {
      closed,
      periods: closed ? [] : current.periods && current.periods.length > 0 ? current.periods : [{ open: '09:00', close: '18:00' }],
    });
  };

  const updateClosures = (closures: SpecialClosure[]) => {
    onChange({ ...config, specialClosures: closures });
  };

  const addClosure = () => {
    updateClosures([
      ...config.specialClosures,
      { name: '', startDate: '', endDate: '', reason: '' },
    ]);
  };

  const updateClosure = (idx: number, patch: Partial<SpecialClosure>) => {
    const next = [...config.specialClosures];
    const cur = next[idx];
    if (!cur) return;
    next[idx] = { ...cur, ...patch };
    updateClosures(next);
  };

  const removeClosure = (idx: number) => {
    updateClosures(config.specialClosures.filter((_, i) => i !== idx));
  };

  const toggleAdvanced = (key: DayKey) => {
    setAdvancedOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateRecurring = (
    key: DayKey,
    patch: Partial<NonNullable<DayHours['recurringRule']>> | null,
  ) => {
    const current = ensureDay(config, key);
    if (patch === null) {
      const { recurringRule: _unused, ...rest } = current;
      void _unused;
      onChange({
        ...config,
        regularHours: { ...config.regularHours, [key]: rest as DayHours },
      });
      return;
    }
    const existing = current.recurringRule ?? { startDate: '', periods: [] };
    updateDay(key, { recurringRule: { ...existing, ...patch } });
  };

  /* ── Validation ──────────────────────────────────────────── */

  const validation = useMemo(() => {
    const errors: string[] = [];
    let hasAnyDay = false;

    for (const { key } of DAYS_ORDERED) {
      const d = config.regularHours[key];
      if (!d) continue;
      if (!d.closed && d.periods && d.periods.length > 0) hasAnyDay = true;
      if (!d.closed && d.periods) {
        d.periods.forEach((p, i) => {
          if (periodInvalid(p)) {
            errors.push(`${d.day}: Periode ${i + 1} ungültig (Start muss vor Ende liegen).`);
          }
        });
      }
      if (d.recurringRule) {
        if (d.recurringRule.startDate && !DATE_RE.test(d.recurringRule.startDate)) {
          errors.push(`${d.day}: Recurring-Rule Startdatum nicht im Format YYYY-MM-DD.`);
        }
        d.recurringRule.periods.forEach((p, i) => {
          if (periodInvalid(p)) {
            errors.push(`${d.day}: Recurring Periode ${i + 1} ungültig.`);
          }
        });
      }
    }

    for (const c of config.specialClosures) {
      if (c.startDate && !DATE_RE.test(c.startDate)) {
        errors.push(`Sonderschließung "${c.name || '(unbenannt)'}": Startdatum ungültig.`);
      }
      if (c.endDate && !DATE_RE.test(c.endDate)) {
        errors.push(`Sonderschließung "${c.name || '(unbenannt)'}": Enddatum ungültig.`);
      }
      if (c.startDate && c.endDate && c.startDate > c.endDate) {
        errors.push(`Sonderschließung "${c.name || '(unbenannt)'}": Startdatum nach Enddatum.`);
      }
    }

    if (!hasAnyDay && config.specialClosures.length === 0) {
      errors.push('Mindestens ein geöffneter Tag oder eine Sonderschließung ist erforderlich.');
    }

    return errors;
  }, [config]);

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <div className="space-y-4">
      {/* Heading / subtitle */}
      <div className="rounded-lg border border-surface-300 bg-surface-100 p-3 space-y-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          Überschrift
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-neutral-400">Titel</span>
            <input
              type="text"
              value={config.title ?? ''}
              onChange={(e) => onChange({ ...config, title: e.target.value })}
              placeholder="Öffnungszeiten"
              maxLength={80}
              className="rounded border border-surface-300 bg-surface-200 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600 focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-neutral-400">Untertitel</span>
            <input
              type="text"
              value={config.subtitle ?? ''}
              onChange={(e) => onChange({ ...config, subtitle: e.target.value })}
              placeholder="Wir sind für Sie erreichbar"
              maxLength={140}
              className="rounded border border-surface-300 bg-surface-200 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600 focus:border-brand-500 focus:outline-none"
            />
          </label>
        </div>
        <p className="text-[11px] text-neutral-500">
          Lasse die Felder leer, um die Standardtexte zu verwenden.
        </p>
      </div>

      <div className="flex gap-1 border-b border-surface-300">
        <TabButton active={tab === 'regular'} onClick={() => setTab('regular')}>
          Reguläre Öffnungszeiten
        </TabButton>
        <TabButton active={tab === 'closures'} onClick={() => setTab('closures')}>
          Sonderschließungen ({config.specialClosures.length})
        </TabButton>
      </div>

      {tab === 'regular' && (
        <div className="space-y-2">
          {DAYS_ORDERED.map(({ key, day, dayShort }) => {
            const d = config.regularHours[key] ?? { day, dayShort, closed: true };
            const periods = d.periods ?? [];
            const canAdd = periods.length < MAX_PERIODS && !d.closed;
            return (
              <div
                key={key}
                className="rounded-lg border border-surface-300 bg-surface-100 p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="w-24 text-sm font-medium text-neutral-200">{d.day}</div>
                  <label className="flex items-center gap-1.5 text-xs text-neutral-400">
                    <input
                      type="checkbox"
                      checked={!!d.closed}
                      onChange={(e) => toggleClosed(key, e.target.checked)}
                      className="rounded border-surface-400 bg-surface-200"
                    />
                    Geschlossen
                  </label>
                  {!d.closed && (
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                      {periods.map((p, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1 rounded bg-surface-200 px-2 py-1"
                        >
                          <input
                            type="time"
                            value={p.open}
                            onChange={(e) => updatePeriod(key, i, { open: e.target.value })}
                            className="bg-transparent text-xs text-neutral-200 focus:outline-none"
                          />
                          <span className="text-neutral-500">–</span>
                          <input
                            type="time"
                            value={p.close}
                            onChange={(e) => updatePeriod(key, i, { close: e.target.value })}
                            className="bg-transparent text-xs text-neutral-200 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => removePeriod(key, i)}
                            className="ml-1 text-neutral-500 hover:text-red-400"
                            aria-label="Periode entfernen"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {canAdd && (
                        <button
                          type="button"
                          onClick={() => addPeriod(key)}
                          className="inline-flex items-center gap-1 rounded bg-surface-300 px-2 py-1 text-xs text-neutral-300 hover:bg-surface-400"
                        >
                          <Plus className="h-3 w-3" />
                          Periode
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => toggleAdvanced(key)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-300"
                >
                  {advancedOpen[key] ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  Erweitert (Recurring Rule)
                </button>
                {advancedOpen[key] && (
                  <RecurringEditor
                    value={d.recurringRule}
                    onChange={(v) => updateRecurring(key, v)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'closures' && (
        <div className="space-y-2">
          {config.specialClosures.length === 0 && (
            <p className="rounded-lg border border-dashed border-surface-300 p-4 text-center text-xs text-neutral-500">
              Keine Sonderschließungen.
            </p>
          )}
          {config.specialClosures.map((c, i) => (
            <div
              key={i}
              className="grid grid-cols-1 gap-2 rounded-lg border border-surface-300 bg-surface-100 p-3 md:grid-cols-[1fr_auto_auto_1fr_auto]"
            >
              <input
                type="text"
                placeholder="Name (z.B. Weihnachten)"
                value={c.name}
                onChange={(e) => updateClosure(i, { name: e.target.value })}
                className="rounded border border-surface-400 bg-surface-200 px-2 py-1.5 text-xs text-neutral-200 focus:border-brand-500 focus:outline-none"
              />
              <input
                type="date"
                value={c.startDate}
                onChange={(e) => updateClosure(i, { startDate: e.target.value })}
                className="rounded border border-surface-400 bg-surface-200 px-2 py-1.5 text-xs text-neutral-200 focus:border-brand-500 focus:outline-none"
              />
              <input
                type="date"
                value={c.endDate}
                onChange={(e) => updateClosure(i, { endDate: e.target.value })}
                className="rounded border border-surface-400 bg-surface-200 px-2 py-1.5 text-xs text-neutral-200 focus:border-brand-500 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Grund (optional)"
                value={c.reason ?? ''}
                onChange={(e) => updateClosure(i, { reason: e.target.value })}
                className="rounded border border-surface-400 bg-surface-200 px-2 py-1.5 text-xs text-neutral-200 focus:border-brand-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => removeClosure(i)}
                className="rounded px-2 py-1 text-neutral-500 hover:bg-surface-300 hover:text-red-400"
                aria-label="Entfernen"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addClosure}
            className="inline-flex items-center gap-1.5 rounded-lg bg-surface-300 px-3 py-2 text-xs text-neutral-200 hover:bg-surface-400"
          >
            <Plus className="h-3.5 w-3.5" />
            Sonderschließung hinzufügen
          </button>
        </div>
      )}

      {validation.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-yellow-900/50 bg-yellow-900/10 p-3 text-xs text-yellow-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <ul className="list-disc space-y-0.5 pl-4">
            {validation.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? 'border-brand-500 text-neutral-100'
          : 'border-transparent text-neutral-500 hover:text-neutral-300'
      }`}
    >
      {children}
    </button>
  );
}

function RecurringEditor({
  value,
  onChange,
}: {
  value: DayHours['recurringRule'];
  onChange: (v: NonNullable<DayHours['recurringRule']> | null) => void;
}) {
  const rule = value ?? { startDate: '', periods: [], exceptLastSaturdayOfMonth: false };
  const addRecurringPeriod = () => {
    onChange({ ...rule, periods: [...rule.periods, { open: '09:00', close: '13:00' }] });
  };
  const updateRecurringPeriod = (i: number, patch: Partial<TimePeriod>) => {
    const periods = [...rule.periods];
    const cur = periods[i];
    if (!cur) return;
    periods[i] = { ...cur, ...patch };
    onChange({ ...rule, periods });
  };
  const removeRecurringPeriod = (i: number) => {
    onChange({ ...rule, periods: rule.periods.filter((_, idx) => idx !== i) });
  };
  return (
    <div className="mt-2 space-y-2 rounded border border-surface-300 bg-surface-200 p-3">
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-neutral-500">Gültig ab:</label>
        <input
          type="date"
          value={rule.startDate}
          onChange={(e) => onChange({ ...rule, startDate: e.target.value })}
          className="rounded border border-surface-400 bg-surface-100 px-2 py-1 text-xs text-neutral-200 focus:border-brand-500 focus:outline-none"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="ml-auto text-[11px] text-neutral-500 hover:text-red-400"
          >
            Regel entfernen
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {rule.periods.map((p, i) => (
          <div key={i} className="flex items-center gap-1 rounded bg-surface-100 px-2 py-1">
            <input
              type="time"
              value={p.open}
              onChange={(e) => updateRecurringPeriod(i, { open: e.target.value })}
              className="bg-transparent text-xs text-neutral-200 focus:outline-none"
            />
            <span className="text-neutral-500">–</span>
            <input
              type="time"
              value={p.close}
              onChange={(e) => updateRecurringPeriod(i, { close: e.target.value })}
              className="bg-transparent text-xs text-neutral-200 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => removeRecurringPeriod(i)}
              className="ml-1 text-neutral-500 hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRecurringPeriod}
          className="inline-flex items-center gap-1 rounded bg-surface-300 px-2 py-1 text-xs text-neutral-300 hover:bg-surface-400"
        >
          <Plus className="h-3 w-3" />
          Periode
        </button>
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-neutral-400">
        <input
          type="checkbox"
          checked={!!rule.exceptLastSaturdayOfMonth}
          onChange={(e) => onChange({ ...rule, exceptLastSaturdayOfMonth: e.target.checked })}
          className="rounded border-surface-400 bg-surface-200"
        />
        Außer letzter Samstag im Monat
      </label>
    </div>
  );
}
